<template>
  <div>
    <el-header>{{ $t('common.finish') }}</el-header>
    <div style="width:50%;float:left;">
      <el-form-item :label="$t('interiorDecorator.pieces')">
        <el-radio-group v-model="curtain.pieces" @change="handlePieceChange">
          <el-radio v-for="item in pieces" :key="item.value" :label="item.value">
            {{ $t(item.label) }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item :label="$t('interiorDecorator.railLength')">
        <input-number
          v-model="curtain.railLengthInCm"
          :precision="0"
          :step="1"
          :min="minRailLength"
          @change="handleRailLengthChange"
        />
      </el-form-item>
      <div v-if="hasTwoPieces()">
        <el-form-item :label="$t('interiorDecorator.widthLeft')">
          <input-number
            v-model="curtain.widthLeftInCm"
            :precision="1"
            :step="0.5"
            :min="minWidth"
            :max="maxWidth"
            :disabled="!hasRailLength()"
            @change="handleWidthLeftChange"
          />
        </el-form-item>
        <el-form-item :label="$t('interiorDecorator.widthRight')">
          <input-number
            v-model="curtain.widthRightInCm"
            :precision="1"
            :step="0.5"
            :min="minWidth"
            :max="maxWidth"
            :disabled="!hasRailLength()"
            @change="handleWidthRightChange"
          />
        </el-form-item>
      </div>
      <el-form-item :label="$t('interiorDecorator.height')">
        <input-number
          v-model="curtain.heightInCm"
          :precision="1"
          :step="0.5"
          :min="25"
          @input="debouncedCalculateOrder"
        />
      </el-form-item>
      <div v-show="!hasTunnelAtTopAndBottom()">
        <el-form-item :label="$t('interiorDecorator.attachLeadCord')">
          <DSESwitch
            v-model="curtain.leadCordYN"
            active-text="common.yes"
            inactive-text="common.no"
            @change="handleLeadCordChange"
          />
        </el-form-item>
        <div v-show="!curtain.leadCordYN">
          <el-form-item :label="$t('interiorDecorator.bottomHem')">
            <el-radio-group v-model="curtain.bottomHem" @change="handleBottomHemChange">
              <el-radio v-for="item in bottomHems" :key="item.value" :label="item.value">
                {{ $t(item.label) }}
              </el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item v-if="hasBottomHem()" :label="$t('interiorDecorator.bottomHemSize')">
            <el-radio-group v-model="curtain.bottomHemSizeInCm" @change="handleBottomHemSizeChange">
              <el-radio :label="10">10</el-radio>
              <el-radio :label="12">12</el-radio>
              <el-radio :label="15">15</el-radio>
              <el-radio :label="20">20</el-radio>
            </el-radio-group>
          </el-form-item>
        </div>
      </div>
      <el-form-item :label="$t('interiorDecorator.tieBack')">
        <el-radio-group v-model="curtain.embraceFinish" @change="handleEmbraceChange">
          <el-radio v-for="item in embraceFinishes" :key="item.value" :label="item.value">
            {{ $t(item.label) }}
          </el-radio>
        </el-radio-group>
      </el-form-item>
    </div>
    <div style="width:50%;float:left">
      <el-form-item :label="$t('interiorDecorator.fixture')">
        <Select
          v-model="curtain.fixture"
          :required="true"
          :translate-labels="true"
          :options="FIXTURES"
          value-field="value"
          label-field="label"
          @input="handleFixtureChange"
        />
      </el-form-item>
      <div v-if="isPleatedCurtain()">
        <el-form-item :label="$t('interiorDecorator.pleat')">
          <Select
            v-model="curtain.pleatType"
            :required="true"
            :translate-labels="true"
            :options="pleatTypes"
            value-field="value"
            label-field="label"
            @input="getFabricWidthFactors"
          />
        </el-form-item>
        <el-form-item v-if="hasPleatingTape()" :label="$t('interiorDecorator.pleatingTapeSize')">
          <el-radio-group v-model="curtain.pleatingTapeSizeInCm">
            <el-radio :label="2.5">2,5</el-radio>
            <el-radio :label="8">8</el-radio>
          </el-radio-group>
        </el-form-item>
      </div>
      <div v-if="isGrommetCurtain()">
        <el-form-item :label="$t('interiorDecorator.grommets')">
          <Select
            v-model="curtain.grommetsProductID"
            :required="true"
            :options="grommets"
            value-field="productID"
            label-field="description"
          />
        </el-form-item>
      </div>
      <div v-if="isWave()">
        <el-form-item :label="$t('interiorDecorator.brackets')">
          <DSESwitch
            v-model="curtain.bracketsYN"
            active-text="orderDraft.bracketsIncluded"
            inactive-text="orderDraft.bracketsExcluded"
          />
        </el-form-item>
        <el-form-item v-if="curtain.bracketsYN" :label="$t('interiorDecorator.waveSize')">
          <el-radio-group v-model="curtain.waveSizeInCm">
            <el-radio :label="6">6</el-radio>
            <el-radio :label="8">8</el-radio>
          </el-radio-group>
        </el-form-item>
      </div>
      <div v-if="hasTunnel()">
        <el-form-item :label="$t('interiorDecorator.tunnelSize')">
          <el-radio-group v-model="curtain.tunnelSizeInCm">
            <el-radio :label="3">3</el-radio>
            <el-radio :label="4">4</el-radio>
            <el-radio :label="5">5</el-radio>
            <el-radio :label="6">6</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item :label="$t('interiorDecorator.tunnelPercentage')">
          <el-radio-group v-model="curtain.tunnelPercentage">
            <el-radio
              v-for="item in tunnelPercentages"
              :key="item.value"
              :label="item.value"
              @change="handleTunnelPercentageChange"
              >{{ $t(item.label) }}</el-radio
            >
          </el-radio-group>
        </el-form-item>
      </div>
      <div v-show="hasBand()">
        <el-form-item :label="$t('interiorDecorator.band')">
          <el-radio-group v-model="curtain.bandSizeInCm" @change="handleBandSizeChange">
            <el-radio :label="6">6</el-radio>
            <el-radio :label="8">8</el-radio>
            <el-radio :label="10">10</el-radio>
            <el-radio :label="12">12</el-radio>
            <el-radio :label="15">15</el-radio>
          </el-radio-group>
        </el-form-item>
      </div>
      <el-form-item :label="$t('interiorDecorator.headingHeight')">
        <input-number v-model="curtain.headingHeightInCm" :precision="1" :step="0.5" :step-strictly="true" />
      </el-form-item>
      <el-form-item :label="$t('orderLine.weights')">
        <DSESwitch v-model="curtain.weightsYN" active-text="common.yes" inactive-text="common.no" />
      </el-form-item>
      <el-form-item :label="$t('orderLine.pleatFactor')">
        <span v-if="curtain.minimumPleatFactor">
          <span style="color:#606266;font-weight:bold">min: </span>
          <span style="margin-right:15px">
            {{ curtain.minimumPleatFactor }}
          </span>
        </span>
        <span v-if="curtain.pleatFactor" style="color:#606266;font-weight:bold">standaard: </span>
        <span style="margin-right:15px">{{ curtain.pleatFactor }}</span>

        <span v-if="curtain.maximumPleatFactor">
          <span style="color:#606266;font-weight:bold">max: </span>
          <span v-if="curtain.maximumPleatFactor">{{ curtain.maximumPleatFactor }}</span>
        </span>
      </el-form-item>
      <div v-if="isRoomHigh()">
        <el-form-item
          :label="hasTwoPieces() ? $t('interiorDecorator.cutSizeLeft') : $t('interiorDecorator.cutSize')"
          :hidden="isRightPiece()"
        >
          <input-number
            v-model="curtain.cutSizeLeftInCm"
            :disabled="curtain.cutSizeLeftInCm === undefined || curtain.cutSizeLeftInCm === null"
            :precision="1"
            :step="0.5"
            :step-strictly="true"
            @input="handleCutSizeLeftChange"
          />
          <div style="padding:0 10px 0 5px;color:red" :hidden="pleatFactorIsWithinBoundaries('Left')">
            {{ $t('orderDraftValidation.pleatFactorRoomHigh') }}
          </div>
          <span style="float:left;padding:0 10px 0 5px;color:#606266;font-weight:bold">
            {{ $t('orderLine.pleatFactor') }}:
          </span>
          {{ curtain.pleatFactorLeft ? curtain.pleatFactorLeft.toFixed(2) : $t('draftTool.tbd') }}
        </el-form-item>
        <el-form-item
          :label="hasTwoPieces() ? $t('interiorDecorator.cutSizeRight') : $t('interiorDecorator.cutSize')"
          :hidden="isLeftPiece()"
        >
          <input-number
            v-model="curtain.cutSizeRightInCm"
            :disabled="curtain.cutSizeRightInCm === undefined || curtain.cutSizeRightInCm === null"
            :precision="1"
            :step="0.5"
            :step-strictly="true"
            @input="handleCutSizeRightChange"
          />
          <div style="padding:0 10px 0 5px;color:red" :hidden="pleatFactorIsWithinBoundaries('Right')">
            {{ $t('orderDraftValidation.pleatFactorRoomHigh') }}
          </div>
          <span style="float:left;padding:0 10px 0 5px;color:#606266;font-weight:bold">
            {{ $t('orderLine.pleatFactor') }}:
          </span>
          {{ curtain.pleatFactorRight ? curtain.pleatFactorRight.toFixed(2) : $t('draftTool.tbd') }}
        </el-form-item>
      </div>
      <div v-else>
        <el-form-item
          :label="hasTwoPieces() ? $t('interiorDecorator.panelsLeft') : $t('interiorDecorator.numberOfPanels')"
          :hidden="isRightPiece()"
        >
          <input-number
            v-model="curtain.panelsLeft"
            :disabled="curtain.panelsLeft === undefined || curtain.panelsLeft === null"
            :precision="2"
            :step="panelStep"
            :step-strictly="true"
            @input="handlePanelsLeftChange"
          />
          <div style="padding:0 10px 0 5px;color:red" :hidden="pleatFactorIsWithinBoundaries('Left')">
            {{ $t('orderDraftValidation.pleatFactorPanel') }}
          </div>
          <span style="padding:0 10px 0 5px;color:#606266;font-weight:bold">
            {{ $t('orderLine.pleatFactor') }}
          </span>
          {{ curtain.pleatFactorLeft ? curtain.pleatFactorLeft.toFixed(2) : $t('draftTool.tbd') }}
        </el-form-item>
        <el-form-item
          :label="hasTwoPieces() ? $t('interiorDecorator.panelsRight') : $t('interiorDecorator.numberOfPanels')"
          :hidden="isLeftPiece()"
        >
          <input-number
            v-model="curtain.panelsRight"
            :disabled="curtain.panelsRight === undefined || curtain.panelsRight === null"
            :precision="2"
            :step="panelStep"
            :step-strictly="true"
            @input="handlePanelsRightChange"
          />
          <div style="padding:0 10px 0 5px;color:red" :hidden="pleatFactorIsWithinBoundaries('Right')">
            {{ $t('orderDraftValidation.pleatFactorPanel') }}
          </div>
          <span style="float:left;padding:0 10px 0 5px;color:#606266;font-weight:bold">{{
            $t('orderLine.pleatFactor')
          }}</span>
          {{ curtain.pleatFactorRight ? curtain.pleatFactorRight.toFixed(2) : $t('draftTool.tbd') }}
        </el-form-item>
        <el-form-item :label="$t('orderLine.panelsForCreation')">
          {{ panelsForCreation || $t('draftTool.tbd') }}
        </el-form-item>
        <el-form-item :label="$t('interiorDecorator.cutSize')">
          {{ cutSizeInCm || $t('draftTool.tbd') }}
        </el-form-item>
      </div>
      <el-form-item :label="$t('orderLine.totalFabricInCm')">
        {{ orderLineDraft.requiredFabricInCm || $t('draftTool.tbd') }}
      </el-form-item>
      <!-- These fields must not be visible, but should be left for easy debugging.
      <el-form-item label="Voering banen links">
        {{ curtain.liningPanelsLeft || '$t('draftTool.tbd').' }}
      </el-form-item>
      <el-form-item label="Voering banen rechts">
        {{ curtain.liningPanelsRight || '$t('draftTool.tbd').' }}
      </el-form-item>
      <el-form-item label="Voering knipmaat links">
        {{ curtain.liningCutSizeLeftInCm || '$t('draftTool.tbd').' }}
      </el-form-item>
      <el-form-item label="Voering knipmaat rechts">
        {{ curtain.liningCutSizeRightInCm || '$t('draftTool.tbd').' }}
      </el-form-item>
      -->
    </div>
  </div>
</template>

<script>
import InputNumber from '@/components/DSE/InputNumber';
import Select from '@/components/DSE/Select';
import DSESwitch from '@/components/DSE/Switch';
import {
  BottomHem,
  CurtainSideHemCorrectionInCm,
  DefaultBandSizeInCm,
  EmbraceFinishes,
  Fixture,
  InteriorDecoratorProductGroup,
  PanelStepHalf,
  PanelStepQuarter,
  Pieces,
  PleatType,
  TunnelAtTopSizeInCm,
  TunnelAtBottomSizeInCm,
  TunnelPercentage
} from '../constants';
import { FabricTypeOption } from '@/utils/constants';
import { roundUpToNearest } from '@/utils/util';

const MIN_WIDTH = 30;
const debounce = require('lodash.debounce');

export default {
  components: {
    InputNumber,
    Select,
    DSESwitch
  },
  props: {
    calculateOrderToggle: {
      type: Boolean
    },
    curtain: {
      type: Object
    },
    generalFinishes: {
      type: Array,
      default: () => []
    },
    grommets: {
      type: Array,
      default: () => []
    },
    orderLineDraft: {
      type: Object
    },
    productGroup: {
      type: Number
    },
    quarterPanels: {
      type: Boolean,
      default: false
    },
    settings: {
      type: Object
    }
  },
  data() {
    return {
      bottomHems: [
        { value: BottomHem.None, label: 'draftTool.hemNone' },
        { value: BottomHem.Single, label: 'draftTool.hemSingle' },
        { value: BottomHem.Double, label: 'draftTool.hemDouble' }
      ],
      embraceFinishes: [
        { value: '', label: 'draftTool.tieBackNone' },
        { value: EmbraceFinishes.Banana.Code, label: 'draftTool.tieBackBanana' },
        { value: EmbraceFinishes.Straight.Code, label: 'draftTool.tieBackStraight' }
      ],
      minWidth: MIN_WIDTH,
      pieces: [
        { value: Pieces.LeftPiece, label: 'draftTool.piecesLeft' },
        { value: Pieces.RightPiece, label: 'draftTool.piecesRight' },
        { value: Pieces.TwoPieces, label: 'draftTool.piecesBoth' }
      ],
      tunnelPercentages: [
        { value: TunnelPercentage.Tight, label: 'draftTool.tight' },
        { value: TunnelPercentage.Pleated, label: 'draftTool.pleated' }
      ]
    };
  },
  computed: {
    cutSizeInCm() {
      const curtain = this.curtain;
      // Cut size left and right are equal if fabric type is panel.
      // Only show cut size of left piece if fabric type is room high and
      // there are two pieces.
      switch (curtain.pieces) {
        case Pieces.RightPiece:
          return curtain.cutSizeRightInCm;
        default:
          return curtain.cutSizeLeftInCm;
      }
    },
    fixture() {
      const curtain = this.curtain;
      return curtain && curtain.fixture;
    },
    maxWidth() {
      return this.curtain.railLengthInCm - this.minWidth;
    },
    minRailLength() {
      return this.hasTwoPieces() ? MIN_WIDTH * 2 : MIN_WIDTH;
    },
    panelsForCreation() {
      const amountLeft = this.curtain.panelsLeft ? Math.ceil(this.curtain.panelsLeft) : 0;
      const amountRight = this.curtain.panelsRight ? Math.ceil(this.curtain.panelsRight) : 0;
      if (amountLeft || amountRight) {
        return amountLeft + amountRight;
      } else {
        return undefined;
      }
    },
    panelStep() {
      return this.quarterPanels ? PanelStepQuarter : PanelStepHalf;
    },
    pleatTypes() {
      if (this.productGroup !== InteriorDecoratorProductGroup.NetCurtainRoomHigh) {
        const index = this.PLEATS.findIndex(item => item.value === PleatType.FlatPleat);
        if (index >= 0) {
          const pleatsCopy = this.PLEATS.slice();
          pleatsCopy.splice(index, 1);
          return pleatsCopy;
        }
      }
      return this.PLEATS;
    }
  },
  watch: {
    calculateOrderToggle() {
      this.debouncedCalculateOrder();
    }
  },
  created() {
    this.debouncedCalculateOrder = debounce(this.calculateOrder, 500);

    this.FIXTURES = [
      { value: Fixture.Rail, label: 'draftTool.fixtureRail' },
      { value: Fixture.Grommets, label: 'draftTool.fixtureGrommets', code: 'AFWRING' },
      { value: Fixture.RodWithRings, label: 'draftTool.fixturePole' },
      { value: Fixture.RodWithSlide, label: 'draftTool.fixtureTunnelAtTop' },
      { value: Fixture.RodWithSlideOnSpan, label: 'draftTool.fixtureTunnelAtTopAndBottom' },
      { value: Fixture.Wave, label: 'draftTool.fixtureWaveCurtain' },
      { value: Fixture.Loop, label: 'draftTool.fixtureTabTop', code: 'AFWLUS' }
    ];
    this.PLEATS = [
      { value: PleatType.SinglePleat, label: 'draftTool.pleatSingle', code: 'AFWPPL1' },
      { value: PleatType.SingleAndReturnPleat, label: 'draftTool.pleatSingleReturn', code: 'AFWPRPE' },
      { value: PleatType.DoublePleat, label: 'draftTool.pleatDouble', code: 'AFWPPL2' },
      { value: PleatType.DoubleAndReturnPleat, label: 'draftTool.pleatDoubleReturn', code: 'AFWPRPD' },
      { value: PleatType.TriplePleat, label: 'draftTool.pleatTriple', code: 'AFWPPL3' },
      { value: PleatType.FlatPleat, label: 'draftTool.pleatFlat', code: 'AFWPPPL' },
      { value: PleatType.PleatingTape, label: 'draftTool.pleatingTape', code: 'AFWPLB' }
    ];
    this.BOTTOM_HEMS = [
      { value: BottomHem.None, label: 'Geen' },
      { value: BottomHem.Single, label: 'Enkel' },
      { value: BottomHem.Double, label: 'Dubbel' }
    ];
  },
  methods: {
    pleatFactorIsWithinBoundaries(side) {
      const curtain = this.curtain;
      return (
        (!curtain.panelsLeft && !curtain.panelsRight && !curtain.cutSizeLeftInCm && !curtain.cutSizeRightInCm) ||
        (curtain['pleatFactor' + side] &&
          curtain['pleatFactor' + side].toFixed(2) >= curtain.minimumPleatFactor &&
          curtain['pleatFactor' + side].toFixed(2) <= curtain.maximumPleatFactor)
      );
    },
    getFabricWidthFactors() {
      const curtain = this.curtain;
      const finishCode = this.getFinishCode();
      if (finishCode) {
        const finish = this.generalFinishes.find(f => f.code === finishCode);
        if (finish) {
          const productGroupCode = this.getProductGroupCode();
          const domainOverride = finish.domainOverrides.find(x => x.productGroupCode === productGroupCode);
          if (productGroupCode && domainOverride && domainOverride.fabricWidthFactor) {
            curtain.pleatFactor = domainOverride.fabricWidthFactor;
            curtain.minimumPleatFactor = domainOverride.minimumFabricWidthFactor;
            curtain.maximumPleatFactor = domainOverride.maximumFabricWidthFactor;
          } else {
            curtain.pleatFactor = finish.fabricWidthFactor;
            curtain.minimumPleatFactor = finish.minimumFabricWidthFactor;
            curtain.maximumPleatFactor = finish.maximumFabricWidthFactor;
          }
          this.debouncedCalculateOrder();
          return;
        }
      }

      this.curtain.panelsLeft = null;
      this.curtain.panelsRight = null;
      this.curtain.cutSizeLeftInCm = null;
      this.curtain.cutSizeRightInCm = null;
      this.curtain.pleatFactor = undefined;
      this.curtain.minimumPleatFactor = undefined;
      this.curtain.maximumPleatFactor = undefined;
      this.curtain.pleatFactorLeft = undefined;
      this.curtain.pleatFactorRight = undefined;
      this.curtain.liningPanelsLeft = null;
      this.curtain.liningPanelsRight = null;
      this.curtain.liningCutSizeLeftInCm = null;
      this.curtain.liningCutSizeRightInCm = null;
      this.orderLineDraft.requiredFabricInCm = null;
    },
    getFinishCode() {
      const curtain = this.curtain;
      switch (curtain.fixture) {
        case Fixture.Grommets:
        case Fixture.Loop: {
          const selectedFixture = this.FIXTURES.find(f => f.value === this.fixture);
          return selectedFixture.code;
        }
        case Fixture.Rail:
        case Fixture.RodWithRings:
          if (curtain.pleatType !== undefined && curtain.pleatType !== null) {
            const pleatCode = this.PLEATS.find(f => f.value === curtain.pleatType).code;
            return pleatCode;
          } else {
            return null;
          }
        case Fixture.Wave:
          if (curtain.bracketsYN) {
            return 'AFWWIH';
          } else {
            return 'AFWWEX';
          }
        case Fixture.RodWithSlide:
          if (curtain.tunnelPercentage === TunnelPercentage.Tight) {
            return 'AFWTNL100';
          } else if (curtain.tunnelPercentage === TunnelPercentage.Pleated) {
            return 'AFWTNL200';
          } else {
            return null;
          }
        case Fixture.RodWithSlideOnSpan:
          if (curtain.tunnelPercentage === TunnelPercentage.Tight) {
            return 'AFWTOS100';
          } else if (curtain.tunnelPercentage === TunnelPercentage.Pleated) {
            return 'AFWTOS200';
          } else {
            return null;
          }
      }
    },
    applyDefaultHeadingHeight(fixture) {
      const code = this.getFixtureCode(fixture);
      if (code) {
        const fixtureOptions =
          this.settings && this.settings.fixtures && this.settings.fixtures.find(elem => elem.code === code);
        this.curtain.headingHeightInCm = (fixtureOptions && fixtureOptions.headingHeightInCm) || undefined;
      } else {
        this.curtain.headingHeightInCm = undefined;
      }
    },
    getFixtureCode(fixture) {
      switch (fixture) {
        case Fixture.Rail:
          return 'BEVRAIL';
        case Fixture.RodWithSlide:
          return 'BEVRMTNL';
        case Fixture.RodWithSlideOnSpan:
          return 'BEVRMTOS';
        case Fixture.RodWithRings:
          return 'BEVRMRING';
        case Fixture.Wave:
          return 'BEVWAVE';
        case Fixture.Loop:
          return 'BEVLUS';
        case Fixture.Grommets:
          return 'BEVRING';
        default:
          return '';
      }
    },
    hasBottomHem() {
      const curtain = this.curtain;
      return curtain && !curtain.leadCordYN && curtain.bottomHem !== BottomHem.None;
    },
    hasEqualParts() {
      const curtain = this.curtain;
      return curtain.pieces === Pieces.TwoPieces && curtain.widthLeftInCm === curtain.widthRightInCm;
    },
    hasPleatingTape() {
      const curtain = this.curtain;
      return curtain && this.isPleatedCurtain() && curtain.pleatType === PleatType.PleatingTape;
    },
    hasRailLength() {
      const curtain = this.curtain;
      const railLength = curtain && curtain.railLengthInCm;
      return railLength !== undefined && railLength !== null;
    },
    hasTunnel() {
      const curtain = this.curtain;
      return curtain && (curtain.fixture === Fixture.RodWithSlide || curtain.fixture === Fixture.RodWithSlideOnSpan);
    },
    hasTunnelAtTopAndBottom() {
      const curtain = this.curtain;
      return curtain && curtain.fixture === Fixture.RodWithSlideOnSpan;
    },
    hasTwoPieces() {
      const curtain = this.curtain;
      return curtain && this.curtain.pieces === Pieces.TwoPieces;
    },
    isGrommetCurtain() {
      const curtain = this.curtain;
      return curtain && curtain.fixture === Fixture.Grommets;
    },
    isLeftPiece() {
      const curtain = this.curtain;
      return curtain && curtain.pieces === Pieces.LeftPiece;
    },
    isLined() {
      return this.orderLineDraft && this.orderLineDraft.liningProductID;
    },
    isPleatedCurtain() {
      const curtain = this.curtain;
      return curtain && (curtain.fixture === Fixture.Rail || curtain.fixture === Fixture.RodWithRings);
    },
    isRightPiece() {
      const curtain = this.curtain;
      return curtain && curtain.pieces === Pieces.RightPiece;
    },
    isRoomHigh() {
      return (
        this.productGroup === InteriorDecoratorProductGroup.NetCurtainRoomHigh ||
        this.productGroup === InteriorDecoratorProductGroup.CurtainRoomHigh
      );
    },
    getProductGroupCode() {
      switch (this.productGroup) {
        case InteriorDecoratorProductGroup.CurtainPanel:
          return 'OG';
        case InteriorDecoratorProductGroup.NetCurtainRoomHigh:
          return 'VI';
        case InteriorDecoratorProductGroup.CurtainRoomHigh:
          return 'KH';
        default:
          return '';
      }
    },
    isWave() {
      const curtain = this.curtain;
      return curtain && curtain.fixture === Fixture.Wave;
    },
    handleCutSizeLeftChange(newValue) {
      const curtain = this.curtain;
      if (this.hasEqualParts() && curtain.cutSizeRightInCm !== newValue) {
        curtain.cutSizeRightInCm = newValue;
        this.setCustomPleatFactor('Right');
        if (this.isLined()) {
          this.updateLiningCalculatedValues('Right');
        }
      }
      this.setCustomPleatFactor('Left');
      if (this.isLined()) {
        this.updateLiningCalculatedValues('Left');
      }
      this.setRequiredFabricRoomHigh();
    },
    handleCutSizeRightChange(newValue) {
      const curtain = this.curtain;
      if (this.hasEqualParts() && curtain.cutSizeLeftInCm !== newValue) {
        curtain.cutSizeLeftInCm = newValue;
        this.setCustomPleatFactor('Left');
        if (this.isLined()) {
          this.updateLiningCalculatedValues('Left');
        }
      }
      this.setCustomPleatFactor('Right');
      if (this.isLined()) {
        this.updateLiningCalculatedValues('Right');
      }
      this.setRequiredFabricRoomHigh();
    },
    updateLiningCalculatedValues(side) {
      const orderLineDraft = this.orderLineDraft;
      const curtain = this.curtain;
      if (this.isRoomHigh()) {
        if (
          orderLineDraft.fabricType === FabricTypeOption.RoomHigh &&
          orderLineDraft.liningFabricType === FabricTypeOption.Panel
        ) {
          curtain['liningPanels' + side] = this.calculatePanels(
            curtain['width' + side + 'InCm'],
            curtain['pleatFactor' + side],
            orderLineDraft.liningWidthInCm
          );
        } else {
          curtain['liningCutSize' + side + 'InCm'] = curtain['cutSize' + side + 'InCm'];
        }
      } else {
        if (
          orderLineDraft.fabricType === FabricTypeOption.Panel &&
          orderLineDraft.liningFabricType === FabricTypeOption.RoomHigh
        ) {
          curtain['liningCutSize' + side + 'InCm'] = this.calculateCutSizeRH(
            curtain['width' + side + 'InCm'],
            curtain['pleatFactor' + side]
          );
        } else if (
          orderLineDraft.fabricType === FabricTypeOption.RoomHigh &&
          orderLineDraft.liningFabricType === FabricTypeOption.Panel
        ) {
          curtain['liningPanels' + side] = this.calculatePanels(
            curtain['width' + side + 'InCm'],
            curtain['pleatFactor' + side],
            orderLineDraft.liningWidthInCm
          );
        } else {
          curtain['liningPanels' + side] = curtain['panels' + side];
        }
      }
    },
    handlePanelsLeftChange(newValue) {
      const curtain = this.curtain;
      if (this.hasEqualParts() && curtain.panelsRight !== newValue) {
        curtain.panelsRight = newValue;
        this.setCustomPleatFactor('Right');
        if (this.isLined()) {
          this.updateLiningCalculatedValues('Right');
        }
      }

      this.setCustomPleatFactor('Left');
      if (this.isLined()) {
        this.updateLiningCalculatedValues('Left');
      }

      this.setRequiredFabricPanel();
    },
    handlePanelsRightChange(newValue) {
      const curtain = this.curtain;

      if (this.hasEqualParts() && curtain.panelsLeft !== newValue) {
        curtain.panelsLeft = newValue;
        this.setCustomPleatFactor('Left');
        if (this.isLined()) {
          this.updateLiningCalculatedValues('Left');
        }
      }

      this.setCustomPleatFactor('Right');
      if (this.isLined()) {
        this.updateLiningCalculatedValues('Right');
      }

      this.setRequiredFabricPanel();
    },
    handlePieceChange(newValue) {
      let railLength = this.curtain.railLengthInCm;
      if (newValue === Pieces.TwoPieces && railLength < MIN_WIDTH * 2) {
        railLength = MIN_WIDTH * 2;
        this.curtain.railLengthInCm = railLength;
      }
      this.handleRailLengthChange(railLength);
    },
    handleRailLengthChange(newValue) {
      if (newValue === undefined || newValue === null) {
        this.curtain.widthLeftInCm = undefined;
        this.curtain.widthRightInCm = undefined;
      } else {
        this.resetWidth(newValue);
      }
      this.debouncedCalculateOrder();
    },
    resetWidth(railLength) {
      let widthLeft;
      let widthRight;
      switch (this.curtain.pieces) {
        case Pieces.TwoPieces:
          widthLeft = railLength / 2;
          widthRight = widthLeft;
          break;
        case Pieces.LeftPiece:
          widthLeft = railLength;
          widthRight = undefined;
          break;
        case Pieces.RightPiece:
          widthLeft = undefined;
          widthRight = railLength;
          break;
        default:
          widthLeft = undefined;
          widthRight = undefined;
          break;
      }
      this.curtain.widthLeftInCm = widthLeft;
      this.curtain.widthRightInCm = widthRight;
    },
    hasBand() {
      return this.hasFixture() && !(this.hasTunnel() || this.hasPleatingTape() || this.isWave());
    },
    handleBandSizeChange() {
      this.debouncedCalculateOrder();
    },
    handleBottomHemChange() {
      this.debouncedCalculateOrder();
    },
    handleBottomHemSizeChange() {
      this.debouncedCalculateOrder();
    },
    handleEmbraceChange() {
      if (this.orderLineDraft.requiredFabricInCm) {
        this.setRequiredFabric();
      }
    },
    handleFixtureChange(value) {
      if (value === Fixture.Wave) {
        this.curtain.bandSizeInCm = DefaultBandSizeInCm;
      }
      this.applyDefaultHeadingHeight(value);
      this.getFabricWidthFactors();
    },
    handleLeadCordChange() {
      this.debouncedCalculateOrder();
    },
    handleTunnelPercentageChange() {
      this.getFabricWidthFactors();
      this.debouncedCalculateOrder();
    },
    handleWidthLeftChange(value) {
      this.curtain.widthRightInCm = this.curtain.railLengthInCm - value || undefined;
      this.debouncedCalculateOrder();
    },
    handleWidthRightChange(value) {
      this.curtain.widthLeftInCm = this.curtain.railLengthInCm - value || undefined;
      this.debouncedCalculateOrder();
    },
    hasFixture() {
      const curtain = this.curtain;
      return curtain && curtain.fixture !== null && curtain.fixture !== undefined;
    },
    getBottomFinishCorrection() {
      const curtain = this.curtain;
      if (this.hasTunnelAtTopAndBottom()) {
        return TunnelAtBottomSizeInCm * 10;
      }
      if (curtain.leadCordYN) {
        return 0;
      }
      const bottomHemSizeInMm = (this.curtain.bottomHemSizeInCm || 0) * 10;
      switch (curtain.bottomHem) {
        case BottomHem.Single:
          return bottomHemSizeInMm;
        case BottomHem.Double:
          return bottomHemSizeInMm * 2;
        default:
          return 0;
      }
    },
    getTopFinishCorrection() {
      if (this.hasPleatingTape() || this.isWave()) {
        return (DefaultBandSizeInCm + 2) * 10;
      } else if (this.hasBand()) {
        return (this.curtain.bandSizeInCm + 2) * 10;
      } else if (this.hasTunnel()) {
        return TunnelAtTopSizeInCm * 10;
      } else {
        return 0;
      }
    },
    calculateOrder() {
      const curtain = this.curtain;
      const orderLineDraft = this.orderLineDraft;

      if (
        !(
          curtain.railLengthInCm &&
          curtain.heightInCm &&
          curtain.pleatFactor &&
          orderLineDraft.fabricProductID &&
          orderLineDraft.fabricWidthInCm
        )
      ) {
        return;
      }

      const command = {
        widthLeftInMm: curtain.widthLeftInCm * 10,
        widthRightInMm: curtain.widthRightInCm * 10,
        heightInMm: curtain.heightInCm * 10,
        isRoomHigh: this.isRoomHigh(),
        isWave: this.isWave(),
        fabric: {
          type: orderLineDraft.fabricType,
          widthInMm: orderLineDraft.fabricWidthInCm * 10,
          isPatterned: orderLineDraft.fabricIsPatterned,
          patternHeightInMm: orderLineDraft.fabricPatternHeightInMm
        },
        lining: null,
        alignPattern: true,
        topFinishCorrectionInMm: this.getTopFinishCorrection(),
        bottomFinishCorrectionInMm: this.getBottomFinishCorrection(),
        sideFinishCorrectionInMm: CurtainSideHemCorrectionInCm * 10,
        fabricWidthFactor: curtain.pleatFactor,
        liningBottomFinishID: orderLineDraft.liningBottomFinishID,
        liningBottomFinishMeasurementInMm: orderLineDraft.liningBottomFinishMeasurementInMm
      };

      if (orderLineDraft.liningProductID) {
        command.lining = {
          type: orderLineDraft.liningFabricType,
          widthInMm: orderLineDraft.liningWidthInCm * 10
        };
      }

      this.$emit('loading', true);
      this.$store
        .dispatch('interiorDecoratorOrder/calculateCurtain', command)
        .then(data => {
          if (this.isRoomHigh()) {
            curtain.cutSizeLeftInCm = data.fabric.cutSizeLeftInCm;
            curtain.cutSizeRightInCm = data.fabric.cutSizeRightInCm;
            if (data.lining) {
              curtain.liningPanelsLeft = data.lining.panelsLeft;
              curtain.liningPanelsRight = data.lining.panelsRight;
              curtain.liningCutSizeLeftInCm = data.lining.cutSizeLeftInCm;
              curtain.liningCutSizeRightInCm = data.lining.cutSizeRightInCm;
            }
            this.setRequiredFabricRoomHigh();
            curtain.pleatFactorLeft = this.calculateCustomPleatFactor('Left');
            curtain.pleatFactorRight = this.calculateCustomPleatFactor('Right');
          } else {
            if (data.lining) {
              // Set these before calling setPleatFactorAndPanels, which might update them.
              curtain.liningPanelsLeft = data.lining.panelsLeft;
              curtain.liningPanelsRight = data.lining.panelsRight;
              curtain.liningCutSizeLeftInCm = data.lining.cutSizeLeftInCm;
              curtain.liningCutSizeRightInCm = data.lining.cutSizeRightInCm;
            }
            if (curtain.pieces === Pieces.RightPiece) {
              curtain.panelsLeft = null;
              curtain.pleatFactorLeft = null;
            } else {
              this.setPleatFactorAndPanels(
                'Left',
                data.fabric.panelsLeft,
                orderLineDraft.fabricWidthInCm,
                curtain.widthLeftInCm,
                curtain.minimumPleatFactor,
                curtain.maximumPleatFactor
              );
            }
            if (curtain.pieces === Pieces.LeftPiece) {
              curtain.panelsRight = null;
              curtain.pleatFactorRight = null;
            } else {
              this.setPleatFactorAndPanels(
                'Right',
                data.fabric.panelsRight,
                orderLineDraft.fabricWidthInCm,
                curtain.widthRightInCm,
                curtain.minimumPleatFactor,
                curtain.maximumPleatFactor
              );
            }
            curtain.cutSizeLeftInCm = data.fabric.cutSizeLeftInCm;
            curtain.cutSizeRightInCm = data.fabric.cutSizeRightInCm;
            this.setRequiredFabricPanel();
          }
        })
        .finally(() => {
          this.$emit('loading', false);
        });
    },
    setPleatFactorAndPanels(side, panels, fabricWidth, width, minimumPleatFactor, maximumPleatFactor) {
      const pleatFactor = this.calculatePleatFactorForPanelCurtain(panels, fabricWidth, width);
      if (pleatFactor > maximumPleatFactor) {
        const newPanelSuggestion = panels - PanelStepHalf; // Never quarters!
        const newPleatFactor = this.calculatePleatFactorForPanelCurtain(newPanelSuggestion, fabricWidth, width);
        if (newPleatFactor >= minimumPleatFactor) {
          this.curtain['panels' + side] = newPanelSuggestion;
          this.curtain['pleatFactor' + side] = newPleatFactor;
          if (this.isLined()) {
            this.updateLiningCalculatedValues(side);
          }
          return;
        }
      }
      this.curtain['panels' + side] = panels;
      this.curtain['pleatFactor' + side] = pleatFactor;
    },
    setCustomPleatFactor(side) {
      this.curtain['pleatFactor' + side] = this.calculateCustomPleatFactor(side);
    },
    calculateCustomPleatFactor(side) {
      if (
        (this.hasTwoPieces() && !this.curtain['width' + side + 'InCm']) ||
        !this.hasRailLength() ||
        !this.curtain.pleatFactor ||
        !this.orderLineDraft.fabricWidthInCm
      ) {
        return undefined;
      }

      if (this.isRoomHigh()) {
        return this.calculatePleatFactorForRoomHighCurtain(
          this.curtain['cutSize' + side + 'InCm'],
          this.curtain['width' + side + 'InCm']
        );
      } else {
        return this.calculatePleatFactorForPanelCurtain(
          this.curtain['panels' + side],
          this.orderLineDraft.fabricWidthInCm,
          this.curtain['width' + side + 'InCm']
        );
      }
    },
    calculatePleatFactorForRoomHighCurtain(cutSize, width) {
      return (cutSize - CurtainSideHemCorrectionInCm) / width;
    },
    calculatePleatFactorForPanelCurtain(panels, fabricWidth, width) {
      return (panels * fabricWidth - CurtainSideHemCorrectionInCm) / width;
    },
    calculateCutSizeRH(finishedWidthInCm, pleatFactor) {
      return roundUpToNearest(finishedWidthInCm * pleatFactor + CurtainSideHemCorrectionInCm, 0.5);
    },
    calculatePanels(finishedWidthInCm, pleatFactor, fabricWidthInCm) {
      return roundUpToNearest((finishedWidthInCm * pleatFactor + CurtainSideHemCorrectionInCm) / fabricWidthInCm, 0.5);
    },
    setRequiredFabric() {
      if (this.isRoomHigh()) {
        this.setRequiredFabricRoomHigh();
      } else {
        this.setRequiredFabricPanel();
      }
    },
    setRequiredFabricPanel() {
      const curtain = this.curtain;
      const cutSize = curtain.cutSizeLeftInCm || curtain.cutSizeRightInCm || 0;
      let panels = 0;
      switch (curtain.pieces) {
        case Pieces.TwoPieces:
          panels = (curtain.panelsLeft || 0) + (curtain.panelsRight || 0);
          break;
        case Pieces.LeftPiece:
          panels = curtain.panelsLeft || 0;
          break;
        case Pieces.RightPiece:
          panels = curtain.panelsRight || 0;
          break;
        default:
          break;
      }
      this.orderLineDraft.requiredFabricInCm = Math.ceil(panels) * cutSize + this.getRequiredFabricEmbrace();
    },
    setRequiredFabricRoomHigh() {
      const curtain = this.curtain;
      let totalCutSize = 0;
      switch (curtain.pieces) {
        case Pieces.TwoPieces:
          totalCutSize = (curtain.cutSizeLeftInCm || 0) + (curtain.cutSizeRightInCm || 0);
          break;
        case Pieces.LeftPiece:
          totalCutSize = curtain.cutSizeLeftInCm || 0;
          break;
        case Pieces.RightPiece:
          totalCutSize = curtain.cutSizeRightInCm || 0;
          break;
        default:
          break;
      }
      this.orderLineDraft.requiredFabricInCm = totalCutSize + this.getRequiredFabricEmbrace();
    },
    getRequiredFabricEmbrace() {
      const height = this.getEmbraceHeight();
      return this.curtain.pieces === Pieces.TwoPieces ? height * 2 : height;
    },
    getEmbraceHeight() {
      switch (this.curtain.embraceFinish) {
        case EmbraceFinishes.Banana.Code:
          return EmbraceFinishes.Banana.HeightInCm;
        case EmbraceFinishes.Straight.Code:
          return EmbraceFinishes.Straight.HeightInCm;
        default:
          return 0;
      }
    }
  }
};
</script>
